import { renderEscPos, toBytes } from '@/lib/printing/escpos'
import { renderReceiptHtml } from '@/lib/printing/receiptHtml'
import type { PrinterAdapter, ReceiptDocument } from '@/lib/printing/types'

/**
 * A phone's Bluetooth printer is physically 57mm — always, regardless of what
 * `Settings → Receipt` says, because that setting exists for the desktop printer, which really
 * does vary between 58mm and 80mm units and is left alone here for exactly that reason. Mobile
 * printing has no such variation to configure, so the two device-bound mobile paths — Bluetooth
 * and the iPhone share sheet — fix it themselves rather than inherit a number that was set for
 * a completely different printer.
 */
function atMobileWidth(receipt: ReceiptDocument): ReceiptDocument {
  return receipt.widthMm === 57 ? receipt : { ...receipt, widthMm: 57 }
}

/* ------------------------------------------------------------------ browser */

/**
 * Prints through the browser's own dialog and the OS printer driver.
 *
 * The default on a PC, and the most reliable path there: the driver already knows the printer,
 * so nothing has to be paired.
 */
export class BrowserPrintAdapter implements PrinterAdapter {
  readonly kind = 'browser' as const
  readonly label = 'Browser print dialog'

  isSupported(): boolean {
    return typeof window !== 'undefined' && typeof window.print === 'function'
  }

  isReady(): boolean {
    return true
  }

  async connect(): Promise<void> {
    // Nothing to pair — the OS owns the printer.
  }

  async print(receipt: ReceiptDocument): Promise<void> {
    // A hidden same-origin iframe rather than a popup: popups get blocked, and printing the
    // main window would print the whole app.
    const frame = document.createElement('iframe')
    frame.setAttribute('aria-hidden', 'true')
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
    document.body.appendChild(frame)

    try {
      const doc = frame.contentDocument
      if (!doc) throw new Error('Could not open a print frame.')

      doc.open()
      doc.write(renderReceiptHtml(receipt))
      doc.close()

      // Give the frame a tick to lay out; printing an empty document otherwise.
      await new Promise((resolve) => setTimeout(resolve, 250))

      frame.contentWindow?.focus()
      frame.contentWindow?.print()

      // The dialog is modal but not awaitable, so the frame is left long enough for the
      // spooler to read it rather than removed immediately.
      await new Promise((resolve) => setTimeout(resolve, 1000))
    } finally {
      frame.remove()
    }
  }
}

/**
 * Sends rendered HTML to the browser's print dialog.
 *
 * The same hidden-iframe path a receipt takes, exposed on its own so labels can use it. A label
 * printer is an ordinary OS printer with an unusual page size — the size lives in the document's
 * own `@page` rule, so nothing here needs to know how big the roll is.
 */
export async function printHtml(html: string): Promise<void> {
  const frame = document.createElement('iframe')
  frame.setAttribute('aria-hidden', 'true')
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
  document.body.appendChild(frame)

  try {
    const doc = frame.contentDocument
    if (!doc) throw new Error('Could not open a print frame.')

    doc.open()
    doc.write(html)
    doc.close()

    await new Promise((resolve) => setTimeout(resolve, 250))

    frame.contentWindow?.focus()
    frame.contentWindow?.print()

    await new Promise((resolve) => setTimeout(resolve, 1000))
  } finally {
    frame.remove()
  }
}

/* ---------------------------------------------------------------- bluetooth */

/** Serial-over-BLE service UUIDs used by the common thermal printer chipsets. */
const BLE_PRINTER_SERVICES = [
  0x18f0, // Widely used by Chinese ESC/POS units (e.g. Goojprt, Xprinter)
  0xff00,
  0xffe0,
  '000018f0-0000-1000-8000-00805f9b34fb',
]

/**
 * Talks ESC/POS straight to a Bluetooth thermal printer.
 *
 * Android Chrome only. iOS Safari has no Web Bluetooth at all — that is a platform decision by
 * Apple, not something a web app can route around, which is why the PDF adapter exists.
 */
export class BluetoothEscPosAdapter implements PrinterAdapter {
  readonly kind = 'bluetooth' as const
  readonly label = 'Bluetooth thermal printer'

  private characteristic: BluetoothRemoteGATTCharacteristic | null = null

  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'bluetooth' in navigator
  }

  isReady(): boolean {
    return this.characteristic !== null
  }

  async connect(): Promise<void> {
    if (!this.isSupported()) {
      throw new Error('This browser cannot use Bluetooth. On iPhone, use the PDF option instead.')
    }

    // Must be triggered by a user gesture; the browser shows its own chooser.
    const device = await navigator.bluetooth.requestDevice({
      filters: BLE_PRINTER_SERVICES.map((service) => ({ services: [service] })),
      optionalServices: BLE_PRINTER_SERVICES,
    })

    const server = await device.gatt?.connect()
    if (!server) throw new Error('Could not connect to the printer.')

    // Vendors differ, so the first writable characteristic on any known service wins rather
    // than hard-coding one model's UUID.
    //
    // Two passes, and the order matters. A printer may expose several writable characteristics,
    // and taking whichever came first can land on the write-without-response channel while an
    // acknowledged one sits beside it unused — losing the flow control that keeps a slow printer
    // from dropping half the receipt. So an acknowledged channel is looked for everywhere first.
    const found =
      (await this.findCharacteristic(server, (c) => c.properties.write)) ??
      (await this.findCharacteristic(server, (c) => c.properties.writeWithoutResponse))

    if (found) {
      this.characteristic = found
      device.addEventListener('gattserverdisconnected', () => {
        this.characteristic = null
      })
      return
    }

    throw new Error('Connected, but this printer exposes no writable channel.')
  }

  private async findCharacteristic(
    server: BluetoothRemoteGATTServer,
    matches: (characteristic: BluetoothRemoteGATTCharacteristic) => boolean,
  ): Promise<BluetoothRemoteGATTCharacteristic | null> {
    for (const uuid of BLE_PRINTER_SERVICES) {
      try {
        const service = await server.getPrimaryService(uuid)
        for (const candidate of await service.getCharacteristics()) {
          if (matches(candidate)) return candidate
        }
      } catch {
        // Service not present on this printer; try the next.
      }
    }

    return null
  }

  async print(receipt: ReceiptDocument): Promise<void> {
    if (!this.characteristic) await this.connect()
    await this.write(renderEscPos(atMobileWidth(receipt)))
  }

  async openDrawer(): Promise<void> {
    if (!this.characteristic) await this.connect()
    await this.write(toBytes([0x1b, 0x70, 0x00, 25, 250]))
  }

  private async write(payload: Uint8Array): Promise<void> {
    const characteristic = this.characteristic
    if (!characteristic) throw new Error('No printer is connected.')

    // 20 bytes, not 180.
    //
    // BLE negotiates an MTU, and the floor every device must support is 23 — three of which are
    // ATT header, leaving 20 for payload. Plenty of printers never negotiate higher. A larger
    // write against such a printer does not fail loudly: `writeValueWithoutResponse` silently
    // truncates to the MTU, and `writeValue` falls back to a long write the cheaper firmware
    // often ignores. Either way bytes vanish with no error, which is why one printer produced a
    // receipt missing its cashier, its customer, its subtotal and its total, all in different
    // places, while another printed the same bytes perfectly — the second simply had a bigger
    // MTU. Sending the guaranteed-safe size costs under a second on a full receipt and works on
    // every printer rather than on the forgiving ones.
    const CHUNK = 20

    // With acknowledgement wherever the printer offers it. Without-response is faster and has no
    // flow control at all: the printer never says it is ready, so anything arriving while its
    // buffer is full is dropped and nothing reports it. Slow, cheap printers are exactly the
    // ones that need the backpressure.
    const acknowledged = characteristic.properties.write

    for (let offset = 0; offset < payload.length; offset += CHUNK) {
      const chunk = payload.slice(offset, offset + CHUNK)

      if (acknowledged) {
        await characteristic.writeValue(chunk)
      } else {
        await characteristic.writeValueWithoutResponse(chunk)
        // Only unacknowledged writes need a pause; an acknowledged one already waited for the
        // printer to take the bytes.
        await new Promise((resolve) => setTimeout(resolve, 20))
      }
    }

    // The bytes are with the printer, but a thermal head is mechanical and still feeding. Ending
    // here and letting the caller disconnect or fire a drawer kick immediately is enough, on a
    // slow printer, to cut the tail off the receipt.
    await new Promise((resolve) => setTimeout(resolve, 120))
  }
}

/* -------------------------------------------------------------------- webusb */

/**
 * Raw ESC/POS over USB, bypassing the print dialog entirely.
 *
 * Opt-in because it needs the printer <em>not</em> claimed by the OS driver: a udev rule on
 * Linux, WinUSB on Windows. The pay-off is no dialog per sale and a working drawer kick.
 */
export class WebUsbEscPosAdapter implements PrinterAdapter {
  readonly kind = 'webusb' as const
  readonly label = 'USB thermal printer (direct)'

  private device: USBDevice | null = null
  private endpoint = 1

  isSupported(): boolean {
    return typeof navigator !== 'undefined' && 'usb' in navigator
  }

  isReady(): boolean {
    return this.device?.opened === true
  }

  async connect(): Promise<void> {
    if (!this.isSupported()) throw new Error('This browser does not support WebUSB.')

    // Class 7 is the USB printer class.
    const device = await navigator.usb.requestDevice({ filters: [{ classCode: 7 }] })
    await device.open()
    if (device.configuration === null) await device.selectConfiguration(1)

    const iface = device.configuration?.interfaces.find((candidate) =>
      candidate.alternate.endpoints.some((endpoint) => endpoint.direction === 'out'),
    )
    if (!iface) throw new Error('That device has no printer interface.')

    await device.claimInterface(iface.interfaceNumber)
    this.endpoint =
      iface.alternate.endpoints.find((candidate) => candidate.direction === 'out')?.endpointNumber ?? 1
    this.device = device
  }

  async print(receipt: ReceiptDocument): Promise<void> {
    if (!this.isReady()) await this.connect()
    await this.device!.transferOut(this.endpoint, renderEscPos(receipt))
  }

  async openDrawer(): Promise<void> {
    if (!this.isReady()) await this.connect()
    await this.device!.transferOut(this.endpoint, toBytes([0x1b, 0x70, 0x00, 25, 250]))
  }
}

/* ----------------------------------------------------------------------- pdf */

/**
 * The iPhone path: render the receipt and hand it to the system share sheet, where AirPrint or
 * the printer vendor's own app can take it.
 *
 * iOS Safari has no Web Bluetooth and no WebUSB, so a browser there can never drive a thermal
 * printer directly. This costs an extra tap and cannot kick a cash drawer — a platform limit,
 * stated plainly rather than hidden.
 */
export class PdfShareAdapter implements PrinterAdapter {
  readonly kind = 'pdf' as const
  readonly label = 'Share / AirPrint (iPhone)'

  isSupported(): boolean {
    return typeof window !== 'undefined'
  }

  isReady(): boolean {
    return true
  }

  async connect(): Promise<void> {
    // Nothing to pair.
  }

  async print(receipt: ReceiptDocument): Promise<void> {
    const html = renderReceiptHtml(atMobileWidth(receipt))
    const blob = new Blob([html], { type: 'text/html' })
    const file = new File([blob], `${receipt.number}.html`, { type: 'text/html' })

    // Web Share with files is the native route into AirPrint and vendor apps.
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: `Receipt ${receipt.number}` })
      return
    }

    // No share sheet, so fall back to opening the receipt for the system print menu.
    const url = URL.createObjectURL(blob)
    const opened = window.open(url, '_blank')
    if (!opened) {
      URL.revokeObjectURL(url)
      throw new Error('Allow pop-ups to print the receipt, or use the browser print option.')
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  }
}

/* -------------------------------------------------------------------- registry */

export function createAdapter(kind: string): PrinterAdapter {
  switch (kind) {
    case 'bluetooth':
      return new BluetoothEscPosAdapter()
    case 'webusb':
      return new WebUsbEscPosAdapter()
    case 'pdf':
      return new PdfShareAdapter()
    default:
      return new BrowserPrintAdapter()
  }
}

/** The sensible default for the device in hand. */
export function suggestAdapterKind(): string {
  if (typeof navigator === 'undefined') return 'browser'

  const agent = navigator.userAgent
  const isIos = /iPad|iPhone|iPod/.test(agent) || (agent.includes('Mac') && 'ontouchend' in document)

  // iOS can do neither Bluetooth nor USB from a browser, so it goes straight to sharing.
  if (isIos) return 'pdf'
  if (/Android/.test(agent) && 'bluetooth' in navigator) return 'bluetooth'
  return 'browser'
}
